import { Autocomplete, SxProps, TextField, Theme } from '@mui/material';
import { useState } from 'react';

import { useServerSearch } from '@/tracker/hooks/useServerSearch';

type Props<T> = {
  value: T | null;
  onChange: (value: T | null) => void;
  /** Стабильная функция поиска (функция модуля или action.bound стора) */
  search: (query: string) => Promise<T[]>;
  getId: (option: T) => string;
  getLabel: (option: T) => string;
  ariaLabel: string;
  placeholder?: string;
  /** Без крестика очистки: значение обязательно (например, текущая доска) */
  disableClearable?: boolean;
  sx?: SxProps<Theme>;
};

/**
 * Выбор одного значения поиском на сервере: варианты грузятся только при открытом списке и
 * по мере ввода, а не всем справочником при открытии экрана
 */
const SearchSelect = <T,>({
  value,
  onChange,
  search,
  getId,
  getLabel,
  ariaLabel,
  placeholder,
  disableClearable,
  sx,
}: Props<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const { setQuery, options, isLoading } = useServerSearch({ search, enabled: isOpen });
  // Выбранное значение держим среди вариантов: иначе MUI считает его невалидным
  const withValue =
    value && !options.some((option) => getId(option) === getId(value))
      ? [value, ...options]
      : options;

  return (
    <Autocomplete
      size="small"
      sx={sx}
      // Типы MUI при disableClearable делают значение non-null; общий onChange остаётся T | null
      disableClearable={disableClearable as false}
      options={withValue}
      // Фильтрует сервер
      filterOptions={(all) => all}
      loading={isLoading}
      loadingText="Поиск…"
      noOptionsText="Ничего не нашлось"
      value={value}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => {
        setIsOpen(false);
        setQuery('');
      }}
      // Подставленное после выбора название (reason 'reset') — не поисковый запрос
      onInputChange={(_, text, reason) => reason === 'input' && setQuery(text)}
      getOptionLabel={getLabel}
      // Ключ по id: у разных пользователей и досок бывают одинаковые названия
      renderOption={({ key: _, ...props }, option) => (
        <li key={getId(option)} {...props}>
          {getLabel(option)}
        </li>
      )}
      isOptionEqualToValue={(option, selected) => getId(option) === getId(selected)}
      onChange={(_, selected) => onChange(selected)}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          slotProps={{ htmlInput: { ...params.inputProps, 'aria-label': ariaLabel } }}
        />
      )}
    />
  );
};

export { SearchSelect };
